import { useForm as createUseForm } from "@ant-design-vue/use";
import lodash from "lodash";
import { reactive } from "vue";
import { setAppPageHeader } from "../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../i18n";
import router from "../../../router";
import store from "../../../store";
import { dateFormat } from "../../../utils/date";
import { initUseForm, onClickEdit } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import { createAppPageHeaderTop, createManageExportCsv, createPage, createRowActionView } from "../../../utils/page";
import { createConditionAction, createConditionForm } from "./event.condition";
import { createColumns, createField } from "./event.section";

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/event";
export const routerPrefix = "events";

export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });

export const onSearch = ({ page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where, sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickView = ({ record }) => {
    router.push({ name: `events-view`, params: { id: record.rev } });
};

export const createSearchOneCriteria = ({ id }) => {
    return {
        select: ["**"],
        page: 0,
        size: 1,
        where: [{ name: "rev", type: "Integer", operator: "=", value: id }],
        children: [{ name: "any", page: 0, size: 1000000 }]
    };
};

export const createForm = () => {
    return {};
};

export const createRules = () => {
    return {};
};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.rev ? `${modelRef.rev} (${modelRef.full_name}) ${dateFormat({ text: modelRef.datetime, format: "DD/MM/YYYY HH:mm" })}` : "";
};

export const loadForm = ({ props, data }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise.then(page => initUseForm({ data, page, routerPrefix })).finally(() => setIsLoading(false));
};

export const initState = ({ props }) => {
    const menuContext = props.menuContext;
    state.manages = [createManageExportCsv({ menuContext })];
    state.manageExtras = [];
    state.rowActions = [createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) })];
};

export const initForm = ({ props, data, refs }) => {
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit })
                }
            });
        }
    });
};

export const getChildren = ({ data }) => {
    return (
        data.useForm?.modelRef?.entity
            ?.split(",")
            .map(d => d.trim())
            .flatMap(entity => {
                return data.useForm.modelRef[entity].map(changeLog => {
                    lodash.cloneDeepWith(changeLog, (value, key, parent) => {
                        if (["refCreateBy", "refUpdateBy"].includes(key)) {
                            delete parent[key];
                        }
                    });
                    return [...changeLog, entity];
                });
            }) || []
    );
};

export const mapping = ({ child }) => {
    const modelName = lodash.camelCase(child[4]);
    let result = child[0];
    result = lodash.mapValues(result, (value, key) => {
        if (key.startsWith("ref")) {
            const ref = lodash.camelCase(key.substring(3));
            return mapping({ child: [value, null, null, null, ref] });
        }
        return value;
    });
    result = lodash.mapKeys(result, (value, key) => {
        if (["id", "version", "isActive", "createBy", "createDate", "updateBy", "updateDate"].includes(key)) {
            return i18n.global.t(`model.${key}`);
        }
        if (i18n.global.te(`model.${modelName}.${key}`)) {
            return i18n.global.t(`model.${modelName}.${key}`);
        }
        return key;
    });
    return result;
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({ options: { sort: [{ name: "datetime", type: "DESC" }] } }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
